import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { Play, Pause, ShoppingBag, Check, ArrowLeft, BookOpen, Eye } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const previewMode = search.get("preview") === "admin";
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const { addItem } = useCart();

  useEffect(() => {
    const url = previewMode && user?.role === "admin" ? `/admin/products/${id}` : `/products/${id}`;
    api.get(url).then((r) => setP(r.data)).catch(() => setP({ _error: true }));
  }, [id, previewMode, user]);

  if (!p) return <div className="max-w-5xl mx-auto px-6 py-20 text-neutral-500">Loading...</div>;
  if (p._error) return <div className="max-w-5xl mx-auto px-6 py-20 text-neutral-500">Product not available.</div>;
  // Merch is sold externally — redirect to the merch gallery.
  if (p.type === "merch") return <Navigate to="/merch" replace />;

  const isMusic = p.type === "music";
  const isEbook = p.type === "ebook";
  const isTemplate = p.type === "template";
  const backHref = isMusic ? "/music" : isTemplate ? "/templates" : "/ebooks";
  const backLabel = isMusic ? "Music" : isTemplate ? "Templates" : "E-books";

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else audioRef.current.play().then(() => setPlaying(true)).catch(() => toast.error("Preview unavailable"));
  };

  const buy = (format, price) => {
    if (previewMode) {
      toast.message("Preview mode — purchases disabled");
      return;
    }
    if (p.status && p.status !== "online") {
      toast.error("This product is not currently available");
      return;
    }
    if (!price) return;
    addItem(p, format, price, {});
    toast.success(`Added ${p.title} to basket`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      {previewMode && (
        <div data-testid="preview-banner" className="mb-8 flex items-center gap-3 px-4 py-3 rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#FCE68A] text-xs tracking-widest uppercase">
          <Eye size={14} /> Admin preview · status: <span className="font-semibold">{p.status || "online"}</span>
        </div>
      )}
      <Link to={backHref} className="inline-flex items-center gap-2 text-neutral-400 hover:text-[#D4AF37] text-sm mb-8">
        <ArrowLeft size={14} /> Back to {backLabel}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6">
          <div className="relative aspect-square overflow-hidden rounded-xl border border-white/10 bg-black">
            <img src={p.cover_image_url} alt={p.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            {isMusic && p.preview_audio_url && (
              <button data-testid="detail-preview-btn" onClick={togglePlay} className="absolute bottom-4 right-4 w-14 h-14 rounded-full bg-[#D4AF37] text-black flex items-center justify-center hover:bg-[#FCE68A]">
                {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
              </button>
            )}
            {isMusic && <audio ref={audioRef} src={p.preview_audio_url} onEnded={() => setPlaying(false)} preload="none" />}
          </div>
          {!isMusic && p.demo_video_url && (
            <div className="mt-6 rounded-xl overflow-hidden border border-white/10">
              <img src={p.demo_video_url} alt="Demo" className="w-full" />
              <div className="text-xs uppercase tracking-widest text-neutral-500 p-3 border-t border-white/10">Demo Preview</div>
            </div>
          )}
        </div>

        <div className="lg:col-span-6">
          <div className="label-eyebrow">{p.category}</div>
          <h1 data-testid="detail-title" className="font-serif-display text-4xl lg:text-5xl text-white mt-3 leading-tight">{p.title}</h1>
          <p className="text-neutral-300 mt-6 leading-relaxed text-lg">{p.description}</p>

          {isMusic && (
            <div className="mt-10 grid grid-cols-2 gap-4">
              <button data-testid="detail-buy-mp3" onClick={() => buy("mp3", p.price_mp3)} className="btn-ghost-gold rounded p-6 text-left">
                <div className="text-[10px] tracking-widest uppercase text-neutral-400">MP3 · 320kbps</div>
                <div className="font-serif-display text-3xl text-white mt-2">£{Number(p.price_mp3).toFixed(2)}</div>
                <div className="mt-3 text-xs tracking-widest uppercase text-[#D4AF37] flex items-center gap-2"><ShoppingBag size={14} /> Add to basket</div>
              </button>
              <button data-testid="detail-buy-wav" onClick={() => buy("wav", p.price_wav)} className="btn-gold rounded p-6 text-left">
                <div className="text-[10px] tracking-widest uppercase">WAV · Lossless</div>
                <div className="font-serif-display text-3xl mt-2">£{Number(p.price_wav).toFixed(2)}</div>
                <div className="mt-3 text-xs tracking-widest uppercase flex items-center gap-2"><ShoppingBag size={14} /> Buy WAV</div>
              </button>
            </div>
          )}

          {(isTemplate || isEbook) && (
            <div className="mt-10">
              <div className="text-[10px] tracking-widest uppercase text-neutral-400">{p.file_type || (isEbook ? "PDF" : "PBIX")}</div>
              <div className="font-serif-display text-5xl text-[#D4AF37] mt-1">£{Number(p.price).toFixed(2)}</div>
              <button data-testid={`detail-buy-${p.type}`} onClick={() => buy(isEbook ? "ebook" : "template", p.price)} className="btn-gold mt-6 px-8 py-4 rounded text-sm tracking-widest uppercase font-semibold inline-flex items-center gap-2">
                {isEbook ? <BookOpen size={16} /> : <ShoppingBag size={16} />} {isEbook ? "Buy E-book" : "Buy Template"}
              </button>
            </div>
          )}

          {p.whats_included && p.whats_included.length > 0 && (
            <div className="mt-12 border-t border-white/10 pt-8">
              <div className="label-eyebrow mb-4">What's Included</div>
              <ul className="space-y-3">
                {p.whats_included.map((w) => (
                  <li key={w} className="flex items-start gap-3 text-neutral-200">
                    <Check size={16} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
